import {
  defaultSystemCode,
  exists,
  SystemCode,
  SystemCodeFromJSON,
  UserMemo,
  UserMemoFromJSON,
  UserNote,
  UserNoteFromJSON
} from '@/api'
import {
  dateTimeToDateFormat,
  dateTimeToPlainFormat,
  dateToPlainFormat
} from '@/utils'
import { BibliographyClass, CollectionFlag, Prefix } from '@/types'

/**
 * 利用者の予約連絡情報の利用者情報
 */
export interface UserReserveContactUser {
  id: string
  name: string
  nameKana: string
  phoneNumber: string
  phoneType: SystemCode
  reserveContactTime: string
  memos: UserMemo[]
  notes: UserNote[]
  revision: number
  userType: SystemCode
}

export const defaultUserReserveContactUser = (): UserReserveContactUser => ({
  id: '',
  name: '',
  nameKana: '',
  phoneNumber: '',
  phoneType: defaultSystemCode(),
  reserveContactTime: '',
  memos: [],
  notes: [],
  revision: 0,
  userType: defaultSystemCode()
})

export function UserReserveContactUserFromJSON(
  json: any
): UserReserveContactUser {
  return UserReserveContactUserFromJSONTyped(json)
}

export function UserReserveContactUserFromJSONTyped(
  json: any
): UserReserveContactUser {
  if (json === undefined || json === null) return json

  return {
    id: exists(json, 'id') ? json.id : '',
    name: exists(json, 'name') ? json.name : '',
    nameKana: exists(json, 'name_kana') ? json.name_kana : '',
    phoneNumber: exists(json, 'phone_number') ? json.phone_number : '',
    phoneType: exists(json, 'phone_type')
      ? SystemCodeFromJSON(json.phone_type)
      : defaultSystemCode(),
    reserveContactTime: exists(json, 'reserve_contact_time')
      ? dateTimeToPlainFormat(json.reserve_contact_time)
      : '',
    memos: exists(json, 'memos')
      ? json.memos.map(UserMemoFromJSON)
      : defaultSystemCode(),
    notes: exists(json, 'notes')
      ? json.notes.map(UserNoteFromJSON)
      : defaultSystemCode(),
    revision: exists(json, 'revision') ? json.revision : 0,
    userType: exists(json, 'user_type') ? json.user_type : defaultSystemCode()
  }
}

/**
 * 利用者の予約連絡情報の予約資料情報
 */
export interface UserReserveContactReserveCollection {
  bibliographyClass: BibliographyClass
  prefix: Prefix
  searchType: SystemCode
  displayMaterialFormat: SystemCode
  displayTitle: string
  displayAuthor: string
  id: number
  collectionNo: string
  reserveId: number
  reserveRevision: number
  reserveRegistrationTime: string
  collectionsUpdatedTime: string
  reserveExpiration: string
  excessDays: number
  displayMemo: string
  reserveRegistrationBranchCode: SystemCode
  forwardingBranchCode: SystemCode
  forwardingPlaceCode: SystemCode
  reserveContactCompleted: boolean
  reserveContactTime: string
  reserveContactCount: number
  collectionFlag: CollectionFlag
  reserveContactMethod: SystemCode
}

export function UserReserveContactReserveCollectionFromJSON(
  json: any
): UserReserveContactReserveCollection {
  return {
    bibliographyClass: exists(json, 'bibliography_class')
      ? json.bibliography_class
      : BibliographyClass.Real,
    prefix: exists(json, 'prefix') ? json.prefix : Prefix.Set,
    searchType: exists(json, 'search_type')
      ? SystemCodeFromJSON(json.search_type)
      : defaultSystemCode(),
    displayMaterialFormat: exists(json, 'display_material_format')
      ? SystemCodeFromJSON(json.display_material_format)
      : defaultSystemCode(),
    displayTitle: exists(json, 'display_title') ? json.display_title : '',
    displayAuthor: exists(json, 'display_author') ? json.display_author : '',
    id: exists(json, 'id') ? json.id : 0,
    collectionNo: exists(json, 'collection_no') ? json.collection_no : '',
    reserveId: exists(json, 'reserve_id') ? json.reserve_id : 0,
    reserveRevision: exists(json, 'reserve_revision')
      ? json.reserve_revision
      : 0,
    reserveRegistrationTime: exists(json, 'reserve_registration_time')
      ? dateTimeToDateFormat(
          dateTimeToPlainFormat(json.reserve_registration_time)
        )
      : '',
    collectionsUpdatedTime: exists(json, 'collections_updated_time')
      ? dateTimeToDateFormat(
          dateTimeToPlainFormat(json.collections_updated_time)
        )
      : '',
    reserveExpiration: exists(json, 'reserve_expiration')
      ? dateToPlainFormat(json.reserve_expiration)
      : '',
    excessDays: exists(json, 'excess_days') ? json.excess_days : 0,
    displayMemo: exists(json, 'display_memo') ? json.display_memo : '',
    reserveRegistrationBranchCode: exists(
      json,
      'reserve_registration_branch_code'
    )
      ? SystemCodeFromJSON(json.reserve_registration_branch_code)
      : defaultSystemCode(),
    forwardingBranchCode: exists(json, 'forwarding_branch_code')
      ? SystemCodeFromJSON(json.forwarding_branch_code)
      : defaultSystemCode(),
    forwardingPlaceCode: exists(json, 'forwarding_place_code')
      ? SystemCodeFromJSON(json.forwarding_place_code)
      : defaultSystemCode(),
    reserveContactCompleted: exists(json, 'reserve_contact_completed')
      ? json.reserve_contact_completed
      : false,
    reserveContactTime: exists(json, 'reserve_contact_time')
      ? dateTimeToDateFormat(dateTimeToPlainFormat(json.reserve_contact_time))
      : '',
    reserveContactCount: exists(json, 'reserve_contact_count')
      ? json.reserve_contact_count
      : 0,
    collectionFlag: exists(json, 'collection_flag')
      ? json.collection_flag
      : CollectionFlag.NotCollection,
    reserveContactMethod: exists(json, 'reserve_contact_method')
      ? SystemCodeFromJSON(json.reserve_contact_method)
      : defaultSystemCode()
  }
}

/**
 * 利用者の予約連絡情報
 */
export interface UserReserveContact {
  user: UserReserveContactUser
  reserveCollections: UserReserveContactReserveCollection[]
}

export const defaultUserReserveContact = (): UserReserveContact => ({
  user: defaultUserReserveContactUser(),
  reserveCollections: []
})

export function UserReserveContactFromJSON(json: any): UserReserveContact {
  return {
    user: exists(json, 'user')
      ? UserReserveContactUserFromJSON(json.user)
      : defaultUserReserveContactUser(),
    reserveCollections: exists(json, 'reserve_collections')
      ? json.reserve_collections.map(
          UserReserveContactReserveCollectionFromJSON
        )
      : []
  }
}

/**
 * 予約連絡状態更新リクエスト
 */
export interface UpdateReserveContactCompleteRequest {
  reserves: {
    id: number
    revision: number
  }[]
  reserveContactComplete: boolean
  _isClearReserveContactInfo: boolean
}

export function UpdateReserveContactCompleteRequestToJSON(
  value: UpdateReserveContactCompleteRequest
): any {
  return {
    reserves: value.reserves.map(reserve => {
      return {
        id: reserve.id,
        revision: reserve.revision
      }
    }),
    reserve_contact_complete: value.reserveContactComplete,
    _is_clear_reserve_contact_info: value._isClearReserveContactInfo
  }
}
